import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Filter, RefreshCw, Loader2, ShieldCheck, MapPin, Coins, Check } from "lucide-react";
import { listOrders, fetchStats, deleteOrder, exportCsvUrl, fetchConfig, fetchSpokes } from "@/lib/api";
import { OrdersTable } from "@/components/OrdersTable";
import { StatsCharts } from "@/components/StatsCharts";
import { CommissionsModal } from "@/components/CommissionsModal";
import { EditOrderModal } from "@/components/EditOrderModal";
import { useAuth } from "@/context/AuthContext";

const LOCATIONS = ["Aylesbury", "Whitehouse", "Bletchley", "Glebefarm", "Hatfield", "Uxbridge"];
const BASE_LOCATIONS = LOCATIONS;

// Same palette used in OrdersTable's SpokePill for visual consistency.
// Dynamic franchise colours override these at runtime via /api/spokes.
const DEFAULT_SPOKE_COLORS = {
  Aylesbury:  { bg: "#DF513B", fg: "#FFFFFF" },
  Whitehouse: { bg: "#2D5C8F", fg: "#FFFFFF" },
  Bletchley:  { bg: "#4F7355", fg: "#FFFFFF" },
  Glebefarm:  { bg: "#E8B241", fg: "#1A1714" },
  Hatfield:   { bg: "#7E3C8A", fg: "#FFFFFF" },
  Uxbridge:   { bg: "#C44A6A", fg: "#FFFFFF" },
};
const SPOKE_COLORS = DEFAULT_SPOKE_COLORS;

// Local calendar date so filters match orders logged in the user's local
// time. (Using toISOString() would return the UTC date, which mismatches
// Eftpos/NewOrder `requested_at` values that carry local hours around
// midnight boundaries — e.g. BST at 00:30 = UTC 23:30 prev day.)
const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const StatTile = ({ label, value, accent = false, testId }) => (
  <div
    className={`brutal-card p-5 ${accent ? "bg-dotr-ink text-white" : ""}`}
    data-testid={testId}
  >
    <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mb-1 ${accent ? "text-white/70" : "text-dotr-inkmuted"}`}>
      {label}
    </div>
    <div className={`font-heading font-semibold text-3xl tracking-tighter ${accent ? "text-white" : "text-dotr-primary"}`}>
      {value}
    </div>
  </div>
);

const defaultFilters = () => ({
  locations: [],
  pickup_delivery: "",
  meal: "",
  date_from: todayISO(),
  date_to: todayISO(),
});

export const AdminConsole = ({ refreshSignal }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filters, setFilters] = useState(() => defaultFilters());
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [spokeLocations, setSpokeLocations] = useState(BASE_LOCATIONS);
  const [spokeColors, setSpokeColors] = useState(DEFAULT_SPOKE_COLORS);
  const [loading, setLoading] = useState(false);
  const [showCommissions, setShowCommissions] = useState(false);
  const [editing, setEditing] = useState(null);

  // Serialise filters to backend-friendly params: `locations` array → `location=a,b`
  const apiParams = useCallback(() => {
    const p = {};
    if (filters.locations && filters.locations.length > 0) {
      p.location = filters.locations.join(",");
    }
    if (filters.pickup_delivery) p.pickup_delivery = filters.pickup_delivery;
    if (filters.meal) p.meal = filters.meal;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = apiParams();
      const [list, s] = await Promise.all([listOrders(params), fetchStats(params)]);
      setOrders(list);
      setStats(s);
    } catch (e) {
      toast.error("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, [apiParams]);

  // Dynamic spoke list + colours — includes admin-added franchises
  useEffect(() => {
    fetchConfig()
      .then((c) => {
        if (Array.isArray(c?.spoke_locations) && c.spoke_locations.length) {
          setSpokeLocations(c.spoke_locations);
        }
      })
      .catch(() => {});
    fetchSpokes()
      .then((d) => {
        if (Array.isArray(d?.spokes) && d.spokes.length) {
          const map = { ...DEFAULT_SPOKE_COLORS };
          for (const s of d.spokes) map[s.name] = { bg: s.color, fg: s.fg };
          setSpokeColors(map);
        }
      })
      .catch(() => {});
  }, [refreshSignal]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const update = (k) => (e) => {
    setFilters((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this order? This cannot be undone.")) return;
    try {
      await deleteOrder(id);
      toast.success("Order deleted.");
      load();
    } catch {
      toast.error("Could not delete order.");
    }
  };

  const handleExport = () => {
    const url = exportCsvUrl(apiParams());
    window.open(url, "_blank", "noopener");
  };

  const toggleLocation = (loc) => {
    setFilters((f) => {
      const set = new Set(f.locations || []);
      if (set.has(loc)) set.delete(loc);
      else set.add(loc);
      return { ...f, locations: Array.from(set) };
    });
  };

  const summaryTiles = useMemo(() => {
    if (!stats) return null;
    const lunch = stats.meal_split?.find((m) => m.name === "Lunch")?.value || 0;
    const dinner = stats.meal_split?.find((m) => m.name === "Dinner")?.value || 0;
    const sym = stats.currency_symbol || "£";
    const fmt = (n) => `${sym}${Number(n || 0).toFixed(2)}`;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="stats-tiles">
        <StatTile label="Orders in range" value={stats.total} testId="stat-total" />
        <StatTile label="Revenue in range" value={fmt(stats.total_revenue)} accent testId="stat-revenue" />
        <StatTile label="Lunch" value={lunch} testId="stat-lunch" />
        <StatTile label="Dinner" value={dinner} accent testId="stat-dinner" />
      </div>
    );
  }, [stats]);

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-1 flex items-center gap-2">
            Console / Reporting
            {isAdmin ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-dotr-primary text-white" data-testid="scope-badge">
                <ShieldCheck size={11} /> All Spokes
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-dotr-ink text-white" data-testid="scope-badge">
                <MapPin size={11} /> {user?.spoke} only
              </span>
            )}
          </div>
          <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight uppercase leading-[0.95]">
            Admin Console
          </h1>
        </div>
        <div className="flex gap-3">
          <button onClick={load} data-testid="btn-refresh" className="brutal-btn-ghost px-4 py-2.5 text-xs flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          {isAdmin && (
            <button onClick={handleExport} data-testid="btn-export-csv" className="brutal-btn px-4 py-2.5 text-xs flex items-center gap-2">
              <Download size={14} />
              Export CSV
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowCommissions(true)} data-testid="btn-commissions" className="brutal-btn px-4 py-2.5 text-xs flex items-center gap-2">
              <Coins size={14} />
              Commissions
            </button>
          )}
        </div>
      </div>

      {showCommissions && <CommissionsModal onClose={() => setShowCommissions(false)} />}

      {summaryTiles}

      <div className="brutal-card p-5 md:p-6 mb-6" data-testid="filters-card">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} />
          <h3 className="font-heading font-semibold text-lg uppercase tracking-wider">Filters</h3>
        </div>

        {isAdmin && (
          <div className="mb-4" data-testid="filter-locations-group">
            <label className="label-uppercase mb-1.5 block">
              Locations {filters.locations.length > 0 && (
                <span className="ml-2 font-mono text-[10px] text-dotr-inkmuted normal-case tracking-normal">
                  ({filters.locations.length} selected — leave empty for all)
                </span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {spokeLocations.map((l) => {
                const active = filters.locations.includes(l);
                const c = spokeColors[l] || { bg: "#1A1714", fg: "#FFFFFF" };
                return (
                  <button
                    type="button"
                    key={l}
                    onClick={() => toggleLocation(l)}
                    data-testid={`filter-loc-chip-${l}`}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-dotr-ink font-mono text-xs uppercase tracking-widest transition-all ${
                      active
                        ? "shadow-brutalSm translate-x-[1px] translate-y-[1px]"
                        : "bg-white text-dotr-ink hover:shadow-brutalSm hover:translate-x-[-1px] hover:translate-y-[-1px]"
                    }`}
                    style={active ? { background: c.bg, color: c.fg } : undefined}
                  >
                    {active && <Check size={12} />}
                    {l}
                  </button>
                );
              })}
              {filters.locations.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, locations: [] }))}
                  data-testid="btn-clear-locations"
                  className="font-mono text-[11px] uppercase tracking-widest text-dotr-inkmuted hover:text-dotr-primary px-2 py-1"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="label-uppercase mb-1.5 block">Mode</label>
            <select data-testid="filter-mode" className="brutal-input h-11 px-3 w-full font-body" value={filters.pickup_delivery} onChange={update("pickup_delivery")}>
              <option value="">All</option>
              <option value="Pickup">Pickup</option>
              <option value="Delivery">Delivery</option>
            </select>
          </div>
          <div>
            <label className="label-uppercase mb-1.5 block">Meal</label>
            <select data-testid="filter-meal" className="brutal-input h-11 px-3 w-full font-body" value={filters.meal} onChange={update("meal")}>
              <option value="">All</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
            </select>
          </div>
          <div>
            <label className="label-uppercase mb-1.5 block">From</label>
            <input data-testid="filter-date-from" type="date" className="brutal-input h-11 px-3 w-full font-body" value={filters.date_from} onChange={update("date_from")} />
          </div>
          <div>
            <label className="label-uppercase mb-1.5 block">To</label>
            <input data-testid="filter-date-to" type="date" className="brutal-input h-11 px-3 w-full font-body" value={filters.date_to} onChange={update("date_to")} />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => setFilters(defaultFilters())} data-testid="btn-clear-filters" className="brutal-btn-ghost px-4 py-2 text-xs">
            Reset to Today
          </button>
        </div>
      </div>

      <StatsCharts stats={stats} />

      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading font-semibold text-2xl uppercase tracking-tight">All Orders</h3>
        <span className="font-mono text-xs text-dotr-inkmuted" data-testid="orders-count">
          {orders.length} {orders.length === 1 ? "result" : "results"}
        </span>
      </div>

      <OrdersTable
        orders={orders}
        onDelete={remove}
        onChanged={load}
        onEdit={isAdmin ? (o) => setEditing(o) : undefined}
        spokeColors={spokeColors}
      />

      {editing && (
        <EditOrderModal
          order={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
